import {
  LayoutDashboard,
  FileAudio,
  ShieldAlert,
  FolderOpen,
  BarChart3,
  PhoneMissed,
  MessagesSquare,
  ThumbsUp,
  Award,
  CheckSquare,
  BookOpen,
  GraduationCap,
  Settings,
  Shield,
  Workflow,
  Sparkles,
  Handshake,
  ListChecks,
  type LucideIcon,
} from 'lucide-react';
import { isAssistantScopeReleased } from '@aica/api-types/schemas';
import type { FeatureFlags, FeatureGateState } from '@/contexts/FeatureFlagsContext';

export interface NavItem {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  href: string;
  matchPatterns?: string[];
  badge?: 'records' | 'alerts';
  roles?: string[];
  featureFlag?: keyof FeatureFlags;
  /**
   * Keep the item hidden from navigation while allowing its route to render an
   * explicit unavailable state. Use only for unfinished surfaces whose route
   * contains no protected or misleading content when the flag is disabled.
   */
  allowDirectAccessWhenDisabled?: boolean;
}

export interface NavSection {
  id: string;
  labelKey?: string;
  items: NavItem[];
  roles?: string[];
  collapsible?: boolean;
}

export const SIDEBAR_SECTIONS: NavSection[] = [
  // Block 1 — Main (not collapsible)
  {
    id: 'main',
    items: [
      {
        id: 'dashboard',
        labelKey: 'nav.dashboard',
        icon: LayoutDashboard,
        href: '/dashboard',
      },
      {
        id: 'records',
        labelKey: 'nav.records',
        icon: FileAudio,
        href: '/records',
      },
      // The deal queue (#766): dark until the org flag is on. Not in the
      // bottom nav — Aelo is desktop-only; the palette and CRM links reach it.
      // A direct link with the flag off reaches the screens themselves: the
      // server's 403 says whether the plan is below Pro (the plans) or the
      // switch is off (the administrator) — the generic root gate cannot.
      {
        id: 'deals',
        labelKey: 'nav.deals',
        icon: Handshake,
        href: '/deals',
        featureFlag: 'enableDealStateSummary',
        allowDirectAccessWhenDisabled: true,
      },
      {
        id: 'alerts',
        labelKey: 'nav.alerts',
        icon: ShieldAlert,
        href: '/alerts',
        badge: 'alerts',
        featureFlag: 'enableAlerts',
        // No `roles` here on purpose. The audience is stated once, in
        // `SCREEN_ACCESS.alerts` (lib/route-permissions.ts), which AppSidebar
        // applies one line BEFORE this filter and which the route guard reads
        // too. A copy here could never change the outcome, and it is exactly
        // the kind of copy that goes stale in the wrong direction: widening
        // SCREEN_ACCESS alone would silently not show the item.
      },
      { id: 'projects', labelKey: 'nav.projects', icon: FolderOpen, href: '/projects' },
      // The assistant about the whole base (#765 Ф3) — only once the contour
      // is released; its route redirects until then.
      ...(isAssistantScopeReleased('org')
        ? [
            {
              id: 'assistant',
              labelKey: 'nav.assistant',
              icon: Sparkles,
              href: '/assistant',
              featureFlag: 'enableAIAssistant' as const,
            },
          ]
        : []),
    ],
  },

  // Block 2 — Analytics (collapsible)
  {
    id: 'analytics',
    labelKey: 'nav.sections.analytics',
    collapsible: true,
    items: [
      {
        id: 'call-stats',
        labelKey: 'nav.callStatistics',
        icon: BarChart3,
        href: '/analytics/call-stats',
        featureFlag: 'enableAnalytics',
      },
      {
        id: 'missed-calls',
        labelKey: 'nav.missedCalls',
        icon: PhoneMissed,
        href: '/analytics/missed-calls',
        featureFlag: 'enableAnalytics',
      },
      {
        // «Голос клиента» (#848): Pro и выше. Прямая ссылка без флага ведёт на
        // сам раздел, который словами говорит, что он на тарифе Pro, — а не на
        // общий экран «функция выключена»: тариф здесь и есть причина.
        id: 'voice-of-customer',
        labelKey: 'nav.voiceOfCustomer',
        icon: MessagesSquare,
        href: '/analytics/voice',
        featureFlag: 'enableVoiceOfCustomer',
        allowDirectAccessWhenDisabled: true,
      },
      {
        // Steps of the scripts the team does not complete (#879). The data
        // belongs to scripts, so the scripts flag is the screen's flag. The
        // audience is stated once, in `SCREEN_ACCESS['script-steps']`.
        id: 'script-steps',
        labelKey: 'nav.scriptSteps',
        icon: ListChecks,
        href: '/analytics/script-steps',
        featureFlag: 'enableScripts',
      },
    ],
  },

  // Block 3 — Team Performance (collapsible): review, coaching, teasers
  {
    id: 'development',
    labelKey: 'nav.sections.development',
    collapsible: true,
    items: [
      {
        id: 'review-queue',
        labelKey: 'nav.reviewQueue',
        icon: CheckSquare,
        href: '/review-queue',
        // Opt-in beta. The explicit per-org flag controls both the writer and
        // this surface; role visibility is client + admin + supervisor + owner.
        featureFlag: 'enableHumanReview',
      },
      {
        id: 'coaching',
        labelKey: 'nav.coaching',
        icon: GraduationCap,
        href: '/coaching/notes',
        matchPatterns: ['/coaching/notes', '/coaching/sessions'],
        featureFlag: 'enableCoaching',
        // No `roles` here, as with alerts above: `SCREEN_ACCESS.coaching`
        // (supervisor; owners see every screen) said the same for a role, and
        // a copy here would hide the item from a grant holder whose grant
        // opens it (`decideScreen`, #1645).
      },
      {
        id: 'ai-feedback',
        labelKey: 'nav.aiFeedback',
        icon: ThumbsUp,
        href: '/analytics/ai-feedback',
        featureFlag: 'enableAIFeedback',
      },
      {
        id: 'competitions',
        labelKey: 'nav.competitions',
        icon: Award,
        href: '/competitions',
        featureFlag: 'enableCompetitions',
      },
      {
        id: 'ai-tasks',
        labelKey: 'nav.aiTasks',
        icon: CheckSquare,
        href: '/analytics/ai-tasks',
        featureFlag: 'enableAITasks',
        allowDirectAccessWhenDisabled: true,
      },
    ],
  },

  // Block 4 — Configuration (collapsible): evaluation setup, org settings,
  // and platform admin live under one header so nothing dangles below the
  // sections (user decision 2026-07-18). The header is "Configuration", not
  // "Settings", so the C-14 duplicate-label problem does not recur.
  {
    id: 'configuration',
    labelKey: 'nav.sections.configuration',
    collapsible: true,
    items: [
      {
        id: 'analysis-setup',
        labelKey: 'nav.analysisSetup',
        icon: Workflow,
        href: '/analysis-setup',
        matchPatterns: ['/analysis-setup'],
      },
      {
        id: 'knowledge-base',
        labelKey: 'nav.knowledgeBase',
        icon: BookOpen,
        href: '/knowledge-base',
        featureFlag: 'enableKnowledgeBase',
      },
      { id: 'settings', labelKey: 'nav.settings', icon: Settings, href: '/settings' },
      {
        id: 'admin',
        labelKey: 'nav.admin',
        icon: Shield,
        href: '/admin',
        matchPatterns: [
          '/admin/api-keys',
          '/admin/health',
          '/admin/credits',
          '/admin/dashboard',
        ],
        roles: ['owner'],
      },
    ],
  },
];

/**
 * Screen id → gating feature flag, derived from the sidebar config so other
 * surfaces (command palette, etc.) inherit nav gating instead of maintaining
 * a second hand-written map that silently drifts (2026-07-13 UX audit C-4).
 */
export const SCREEN_FEATURE_FLAGS: Readonly<Partial<Record<string, keyof FeatureFlags>>> =
  Object.fromEntries(
    SIDEBAR_SECTIONS.flatMap((section) =>
      section.items.flatMap((item) =>
        item.featureFlag ? [[item.id, item.featureFlag] as const] : []
      )
    )
  );

type RouteFeatureGate = {
  path: string;
  flag: keyof FeatureFlags;
  allowDirectAccessWhenDisabled: boolean;
};

const ROUTE_FEATURE_GATES: RouteFeatureGate[] = [];
for (const section of SIDEBAR_SECTIONS) {
  for (const item of section.items) {
    if (!item.featureFlag) continue;
    // `matchPatterns`, not just `href`. A nav item can own several routes —
    // coaching is one entry with `/coaching/notes` AND `/coaching/sessions` —
    // and gating only the href left the siblings reachable by direct URL with
    // the flag off: `/coaching/sessions` matches neither the exact href nor
    // the `'/coaching/notes/'` prefix, and those route files carry no gate of
    // their own. `enableCoaching` was then default-false everywhere, so that was
    // every organization; since #758 it is on by default and the gate guards an
    // organization that switched it off.
    const paths = new Set([item.href, ...(item.matchPatterns ?? [])]);
    for (const path of paths) {
      ROUTE_FEATURE_GATES.push({
        path,
        flag: item.featureFlag,
        allowDirectAccessWhenDisabled: item.allowDirectAccessWhenDisabled ?? false,
      });
    }
  }
}

/**
 * How a gated menu item (or palette command) appears: `visible` as it is,
 * `locked` — shown with a lock and the plan it is sold from, `hidden`.
 *
 * `tier_not_eligible` is the only verdict that locks: the plan is below the
 * feature's floor, and the customer should learn the feature exists (#1897,
 * the same principle as the «what's new» pop-up). Everything else keeps the
 * flag's own answer — `feature_disabled`, a still-loading map and a failed
 * read all stay hidden, as before. `enabled` is the flag read apart from the
 * verdict (`isEnabled`): the localStorage seed answers it before verdicts
 * arrive, and an item must not blink out of the menu until they do.
 */
export type NavItemAccess = 'visible' | 'locked' | 'hidden';

export function navItemAccess(state: FeatureGateState, enabled: boolean): NavItemAccess {
  if (state === 'tier_not_eligible') return 'locked';
  return enabled ? 'visible' : 'hidden';
}

/**
 * Resolve the feature gate for a route.
 *
 * The two unfinished analytics routes deliberately return `null`: their sidebar
 * entries stay flag-hidden, while a bookmarked URL reaches the honest
 * unavailable page instead of the generic feature-disabled screen.
 *
 * Longest path first, so a nested route cannot be claimed by a shorter
 * sibling's prefix before its own exact entry is considered.
 */
export function getRouteFeatureFlag(pathname: string): keyof FeatureFlags | null {
  const exact = ROUTE_FEATURE_GATES.find((route) => route.path === pathname);
  const matched =
    exact ??
    [...ROUTE_FEATURE_GATES]
      .sort((a, b) => b.path.length - a.path.length)
      .find((route) => pathname.startsWith(`${route.path}/`));

  if (!matched || matched.allowDirectAccessWhenDisabled) return null;
  return matched.flag;
}
